import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Flame, ThermometerSun, Snowflake, Building2, User, Phone, FolderKanban } from 'lucide-react';

const PIPELINE_STAGES = [
  { value: 'neu', label: 'Neu', color: '#9CA3AF' },
  { value: 'kontaktiert', label: 'Kontaktiert', color: '#3B82F6' },
  { value: 'termin', label: 'Termin', color: '#8B5CF6' },
  { value: 'pilot', label: 'Pilot', color: '#F59E0B' },
  { value: 'laufend', label: 'Laufend', color: '#10B981' },
  { value: 'abgelehnt', label: 'Abgelehnt', color: '#EF4444' }
];

const getScoreIcon = (score) => {
  switch (score) {
    case 'heiss': return <Flame className="h-3 w-3 text-red-500" />;
    case 'mittel': return <ThermometerSun className="h-3 w-3 text-orange-400" />;
    case 'kalt': return <Snowflake className="h-3 w-3 text-blue-500" />;
    default: return null;
  }
};

export const Pipeline = () => {
  const navigate = useNavigate();
  const { get, patch } = useApi();
  const { isAdmin } = useAuth();
  const [pipelineData, setPipelineData] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [loading, setLoading] = useState(true);

  const loadCampaigns = useCallback(async () => {
    try {
      const data = await get('/campaigns');
      setCampaigns(data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  }, [get]);

  const loadPipeline = useCallback(async () => {
    try {
      let endpoint = '/pipeline/stats';
      if (selectedCampaign && selectedCampaign !== 'all') {
        endpoint += `?campaign_id=${selectedCampaign}`;
      }
      const data = await get(endpoint);
      setPipelineData(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Pipeline');
    } finally {
      setLoading(false);
    }
  }, [get, selectedCampaign]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await patch(`/leads/${leadId}/status?status=${newStatus}`);
      toast.success('Status aktualisiert');
      loadPipeline();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="grid grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-96 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="pipeline-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Pipeline</h1>
        <div className="flex items-center gap-4">
          {isAdmin && campaigns.length > 0 && (
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger className="w-[200px]" data-testid="pipeline-campaign-filter">
                <FolderKanban className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Kampagne" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kampagnen</SelectItem>
                {campaigns.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Gesamt:</span>
            <span className="font-semibold text-gray-900">
              {pipelineData.reduce((acc, stage) => acc + stage.count, 0)} Leads
            </span>
          </div>
        </div>
      </div>

      {/* Pipeline Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="pipeline-board">
        {PIPELINE_STAGES.map((stage) => {
          const stageData = pipelineData.find(p => p.status === stage.value) || { count: 0, leads: [] };
          return (
            <div 
              key={stage.value} 
              className="kanban-column"
              data-testid={`pipeline-column-${stage.value}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: stage.color }}
                  />
                  <h3 className="font-semibold text-gray-900">{stage.label}</h3>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {stageData.count}
                </Badge>
              </div>

              <div className="space-y-3 min-h-[400px]">
                {stageData.leads.map((lead) => (
                  <div 
                    key={lead.id} 
                    className="kanban-card group"
                    data-testid={`pipeline-card-${lead.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-mono text-xs text-gray-400">{lead.lead_id}</span>
                      {getScoreIcon(lead.lead_score)}
                    </div>
                    
                    <h4 
                      className="font-medium text-gray-900 cursor-pointer hover:text-[#E63946] transition-colors"
                      onClick={() => navigate(`/leads/${lead.id}`)}
                    >
                      {lead.firma}
                    </h4>
                    
                    <div className="mt-2 space-y-1 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span className="truncate">{lead.ansprechpartner}</span>
                      </div>
                      {lead.telefon && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          <span className="truncate">{lead.telefon}</span>
                        </div>
                      )}
                    </div>

                    {lead.naechster_schritt && (
                      <p className="mt-2 text-xs text-[#E63946] truncate">
                        → {lead.naechster_schritt}
                      </p>
                    )}

                    {/* Quick status change */}
                    <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Select 
                        value={lead.lead_status}
                        onValueChange={(value) => handleStatusChange(lead.id, value)}
                      >
                        <SelectTrigger className="h-7 text-xs" data-testid={`status-select-${lead.id}`}>
                          <SelectValue placeholder="Status ändern" />
                        </SelectTrigger>
                        <SelectContent>
                          {PIPELINE_STAGES.map(s => (
                            <SelectItem key={s.value} value={s.value} className="text-xs">
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}

                {stageData.leads.length === 0 && (
                  <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                    Keine Leads
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
